import React, { useState, useEffect, useRef, useMemo } from 'react';
import FloatingIcons from './FloatingIcons';
import MultiAgentConfigVisualization from './MultiAgentConfigVisualization';
import GraphsSection from './GraphsSection';
import MultiAgentChatComparison from './MultiAgentChatComparison';
import MultiAgentOnboarding from './MultiAgentOnboarding';
import HelpTourButton from './guidedTour/HelpTourButton';

import { useOptimizationStore } from '../store/optimizationStore';
import { unifiedWebSocketService } from '../services/unifiedWebSocket';
import type { MultiAgentConfig } from '../services/unifiedWebSocket';
import { useAuthContext } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import NotificationWrapper from './NotificationWrapper';
import ConfirmModal from './ConfirmModal';
import { 
  calculateExponentialProgress, 
  calculateTotalProgress, 
  getElapsedSeconds 
} from '../utils/exponentialProgress';
import { formatOptimizationWebSocketError } from '../utils/errorUtils';
import { getJobStopButtonLabel } from '../utils/jobActionUi';
import { formatOptimizationDatasetNameForApi } from '../utils/optimizationDatasetName';
import './MultiAgentPage.css';
import {
  MAX_AGENT_DESCRIPTION_LENGTH,
  MAX_AGENT_PROMPT_LENGTH,
  MAX_COORDINATOR_PROMPT_LENGTH,
} from '../constants/agentFieldLimits';
import { NOTIFICATION_AUTO_HIDE_MS } from '../constants/uiTiming';
import { mergeConfiguratorModelOptions } from '../utils/configuratorModels';

interface MultiAgentPageProps {
  onShowLoginModal?: () => void;
}

const MultiAgentPage: React.FC<MultiAgentPageProps> = ({ onShowLoginModal }) => {
  // ✅ Используем экспоненциальный прогресс как в single-agent
  const [smoothProgress, setSmoothProgress] = useState(0);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  // Состояние для сообщения о завершении
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
    
    const {
      workers,
    setWorkers,
    coordinatorPrompt,
    coordinatorModel,
    coordinatorTemperature,
    coordinatorDescription,
    setCoordinatorPrompt,
    setCoordinatorModel,
    setCoordinatorTemperature,
    setCoordinatorDescription,
    additionalWorkerConfig,
    loadDefaults,
    availableModels,
    availableDatasets,
    selectedDataset,
    iterations: storeIterations,
    multiAgentIterations: storeMultiAgentIterations,
    setMultiAgentIterations: setStoreMultiAgentIterations,
    setSelectedDataset,
    // Мультиагентные данные
    multiAgentIsOptimizing,
    multiAgentIsOptimized,
    multiAgentOptimizationProgress,
    setMultiAgentIsOptimizing,
    setMultiAgentOptimizationProgress,
    addMultiAgentIterationResult,
    setMultiAgentIterationResults,
    setMultiAgentOptimizedPrompts,
    setMultiAgentIsOptimized,
    processMultiAgentOptimizationResult,
    resetToDefaults,
    isMultiAgentAtDefaults,
    clearMultiAgentChat,
    multiAgentIterationResults,
    // Экспоненциальный прогресс для multi-agent
    multiAgentProgressStartTime,
    setMultiAgentProgressStartTime,
    multiAgentCurrentProgressStage,
    setMultiAgentCurrentProgressStage,
    multiAgentExpectedIterationTime,
    setMultiAgentExpectedIterationTime
  } = useOptimizationStore();
  const [iterations, setIterations] = useState(0);
  const [iterationsInput, setIterationsInput] = useState('');
  const [workerAgents, setWorkerAgents] = useState(2);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const { isAuthenticated } = useAuthContext();
  const { notification, showNotification, hideNotification } = useNotification();

  const isResetDisabled = multiAgentIsOptimizing || isMultiAgentAtDefaults();
  const isCoordinatorPromptInvalid = !coordinatorPrompt.trim();
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isResettingDefaults, setIsResettingDefaults] = useState(false);
  const [multiAgentStopping, setMultiAgentStopping] = useState(false);

  const configuratorModelOptions = useMemo(
    () => mergeConfiguratorModelOptions(availableModels),
    [availableModels]
  );

  // Функция для автоматического изменения размера textarea
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
  };

  useEffect(() => {
    const initializeDefaults = async () => {
      try {
        console.log('🔄 MultiAgentPage: Loading defaults...');
        await loadDefaults();
        console.log('✅ MultiAgentPage: Defaults loaded successfully');
        
        // Проверяем что загрузилось
        const state = useOptimizationStore.getState();
        console.log('📊 MultiAgentPage: Current store state:', {
          coordinatorPrompt: state.coordinatorPrompt,
          coordinatorModel: state.coordinatorModel,
          coordinatorTemperature: state.coordinatorTemperature,
          workers: state.workers,
          availableModels: state.availableModels,
          availableDatasets: state.availableDatasets
        });
      } catch (error) {
        console.error('❌ MultiAgentPage: Failed to load defaults:', error);
      }
    };

    initializeDefaults();

    // Check for stored session and restore if exists
    const checkAndRestoreSession = async () => {
      const { unifiedWebSocketService } = await import('../services/unifiedWebSocket');
      if (unifiedWebSocketService.hasStoredSession()) {
        const runId = unifiedWebSocketService.getStoredRunId();
        const mode = unifiedWebSocketService.getStoredMode();
        const isConnector = unifiedWebSocketService.getStoredIsConnector();
        
        // Восстанавливаем сессию только если это НЕ ConnectorPage сессия
        if (runId && mode === 'multi' && !isConnector) {
          console.log('🔄 MultiAgent: Detected stored session, attempting to restore:', runId);
          try {
            setMultiAgentIsOptimizing(true);
            setShowCompletionMessage(false);
            
            // ✅ Restore progress immediately
            const storedProgress = unifiedWebSocketService.getStoredProgress();
            if (storedProgress > 0) {
              console.log(`📊 MultiAgent restoring progress: ${storedProgress}%`);
              setSmoothProgress(storedProgress);
              setMultiAgentOptimizationProgress(storedProgress);
            }
            
            await unifiedWebSocketService.restore(runId, {
              onProgress: (progressValue) => {
                setMultiAgentOptimizationProgress(progressValue);
              },
              onComplete: async (result) => {
                console.log('✅ MultiAgent restored optimization completed:', result);
                processMultiAgentOptimizationResult(result);
                setMultiAgentIsOptimizing(false);
                setMultiAgentIsOptimized(true);
              },
              onError: (error) => {
                console.error('❌ MultiAgent restoration error:', error);
                setMultiAgentIsOptimizing(false);
                setMultiAgentProgressStartTime(null);
                unifiedWebSocketService.clearSession(); // Clear invalid session
                showNotification(
                  'error',
                  formatOptimizationWebSocketError(
                    error,
                    'Не удалось восстановить оптимизацию. Запустите новую.'
                  )
                );
              },
              onIntermediateResult: (result) => {
                console.log('📝 Processing multi-agent restored intermediate result:', result);
                const resultToStore = {
                  prompt: result.prompt || 'Промпт отсутствует',
                  embedding_point: result.embedding_point,
                  evaluation_point: result.evaluation_point,
                  iteration: result.iteration || (result.seq !== undefined ? result.seq : 1),
                  seq: result.seq, // Передаем seq из WebSocket ответа
                  temperature: result.temperature,
                  "master-agent": result["master-agent"],
                  "worker-agents": result["worker-agents"]
                };
                addMultiAgentIterationResult(resultToStore);
              }
            });
          } catch (error) {
            console.error('❌ Failed to restore multi-agent session:', error);
            setMultiAgentIsOptimizing(false);
            unifiedWebSocketService.clearSession(); // Clear invalid session
          }
        }
      }
    };

    checkAndRestoreSession();
  }, [loadDefaults, setMultiAgentIsOptimizing, setMultiAgentOptimizationProgress, setMultiAgentIsOptimized, processMultiAgentOptimizationResult, addMultiAgentIterationResult]);

  // Синхронизируем workerAgents с количеством воркеров в store
  useEffect(() => {
    setWorkerAgents(workers.length);
  }, [workers]);

  // Если список датасетов обновился — держим валидный выбор в store (одна выпадашка).
  const availableDatasetsKey = availableDatasets.join('\u001f');
  useEffect(() => {
    if (multiAgentIsOptimizing || availableDatasets.length === 0) return;
    if (selectedDataset && availableDatasets.includes(selectedDataset)) return;
    setSelectedDataset(availableDatasets[0]);
  }, [availableDatasetsKey, availableDatasets, selectedDataset, multiAgentIsOptimizing, setSelectedDataset]);

  // Синхронизируем iterations с store
  useEffect(() => {
    if (storeMultiAgentIterations > 0 && iterations !== storeMultiAgentIterations) {
      setIterations(storeMultiAgentIterations);
      setIterationsInput(storeMultiAgentIterations.toString());
    }
  }, [storeMultiAgentIterations, iterations]);

  // Синхронизируем iterationsInput с iterations
  useEffect(() => {
    setIterationsInput(iterations.toString());
  }, [iterations]);

  // Автоматически изменяем размер textarea при загрузке данных
  useEffect(() => {
    const textareas = document.querySelectorAll('textarea');
    textareas.forEach(textarea => {
      autoResizeTextarea(textarea as HTMLTextAreaElement);
    });
  }, [coordinatorPrompt, coordinatorDescription, workers]);

  // ✅ Экспоненциальное обновление прогресса (как в single-agent)
  useEffect(() => {
    if (multiAgentIsOptimizing && multiAgentProgressStartTime !== null) {
      // Обновляем прогресс каждые 100ms для плавности
      progressIntervalRef.current = setInterval(() => {
        const elapsedSeconds = getElapsedSeconds(multiAgentProgressStartTime);
        const stageProgress = calculateExponentialProgress(elapsedSeconds, multiAgentExpectedIterationTime);
        const totalProgress = calculateTotalProgress(multiAgentCurrentProgressStage, iterations || storeMultiAgentIterations, stageProgress);
        
        // ✅ Синхронизируем ВСЕ состояния прогресса
        setSmoothProgress(totalProgress);
        setMultiAgentOptimizationProgress(totalProgress);
      }, 100);

      return () => {
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
        }
      };
    } else {
      // Останавливаем таймер, если оптимизация не идет
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    }
  }, [multiAgentIsOptimizing, multiAgentProgressStartTime, multiAgentCurrentProgressStage, iterations, storeMultiAgentIterations, multiAgentExpectedIterationTime]);

  // Эффект для восстановления прогресса при обновлении страницы
  useEffect(() => {
    if (multiAgentIsOptimizing && multiAgentProgressStartTime !== null) {
      const elapsedSeconds = getElapsedSeconds(multiAgentProgressStartTime);
      const stageProgress = calculateExponentialProgress(elapsedSeconds, multiAgentExpectedIterationTime);
      const totalProgress = calculateTotalProgress(multiAgentCurrentProgressStage, iterations || storeMultiAgentIterations, stageProgress);
      
      setSmoothProgress(totalProgress);
      setMultiAgentOptimizationProgress(totalProgress);
      console.log('🔄 Restored multi-agent progress after page refresh:', {
        elapsedSeconds,
        stageProgress,
        totalProgress,
        multiAgentCurrentProgressStage,
        iterations: iterations || storeMultiAgentIterations
      });
    }
  }, []); // Выполняется только при монтировании

  // Следим за изменением количества результатов итераций
  // ⚠️ ВАЖНО: seq 0 - это baseline evaluation, считаем только seq > 0 (оптимизированные промпты)
  useEffect(() => {
    if (multiAgentIsOptimizing) {
      // Считаем только оптимизированные результаты (seq > 0), исключая baseline (seq 0)
      const optimizedCount = multiAgentIterationResults.filter(r => 
        r.seq !== undefined && r.seq > 0
      ).length;
      const targetIterations = iterations || storeMultiAgentIterations;
      
      console.log(`📊 Multi-agent progress tracking:`, {
        totalResults: multiAgentIterationResults.length,
        optimizedCount,
        targetIterations,
        currentStage: multiAgentCurrentProgressStage
      });
      
      // Обновляем stage только когда получили новый оптимизированный результат
      if (optimizedCount > multiAgentCurrentProgressStage) {
        if (optimizedCount < targetIterations) {
          console.log(`✅ Multi-agent iteration ${optimizedCount}/${targetIterations} completed, moving to stage ${optimizedCount}`);
          setMultiAgentCurrentProgressStage(optimizedCount);
          setMultiAgentProgressStartTime(Date.now());
          
          const exactProgress = (optimizedCount / targetIterations) * 100;
          setSmoothProgress(exactProgress);
          setMultiAgentOptimizationProgress(exactProgress);
        } else if (optimizedCount >= targetIterations) {
          console.log(`✅ All multi-agent iterations completed (${optimizedCount}/${targetIterations})`);
          setSmoothProgress(100);
          setMultiAgentOptimizationProgress(100);
          // НЕ останавливаем оптимизацию здесь - ждем onComplete от WebSocket
        }
      }
    }
  }, [multiAgentIterationResults.length, multiAgentIsOptimizing, multiAgentCurrentProgressStage, iterations, storeMultiAgentIterations, multiAgentIterationResults]);

  // Показываем сообщение о завершении оптимизации
  useEffect(() => {
    if (multiAgentIsOptimized && !multiAgentIsOptimizing) {
      setTimeout(() => {
        setShowCompletionMessage(true);
        console.log('✅ Multi-agent optimization completed, showing completion message');
      }, 500);
    }
    // Не скрываем сообщение здесь, чтобы оно не пропадало при переключении вкладок.
    // Сброс происходит явно при старте новой оптимизации (handleOptimize).
  }, [multiAgentIsOptimized, multiAgentIsOptimizing]);

  const scrollToConfigure = () => {
    const configureSection = document.getElementById('configure-section');
    if (configureSection) {
      configureSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleOptimize = async () => {
    if (!isAuthenticated) {
      if (onShowLoginModal) {
        onShowLoginModal();
      } else {
        alert('Чтобы запустить оптимизацию, войдите в систему.');
      }
      return;
    }

    const workersWithEmptyNames = workers.filter(worker => !worker.name || worker.name.trim().length === 0);
    if (workersWithEmptyNames.length > 0) {
      showNotification('error', 'Имя агента-исполнителя не может быть пустым. Укажите имя перед запуском.');
      return;
    }

    if (!coordinatorPrompt.trim()) {
      showNotification('error', 'Перед запуском укажите промпт координатора.');
      return;
    }

    const workersWithEmptyPrompts = workers.filter(worker => !worker.prompt || worker.prompt.trim().length === 0);
    if (workersWithEmptyPrompts.length > 0) {
      showNotification('error', 'Перед запуском укажите промпт для каждого агента-исполнителя.');
      return;
    }

    const datasetNames = selectedDataset
      ? [selectedDataset]
      : availableDatasets[0]
        ? [availableDatasets[0]]
        : [];
    const datasetNameForRequest = formatOptimizationDatasetNameForApi(datasetNames);
    if (!datasetNameForRequest) {
      showNotification('error', 'Перед запуском выберите датасет.');
      return;
    }

    try {
      console.log('🚀 Starting multi-agent optimization...');
      
      // Проверяем iterations: если пустое или невалидное, устанавливаем 1 и показываем уведомление
      // Проверяем как значение iterations, так и строку iterationsInput
      let actualIterations = iterations;
      const isEmptyOrInvalid = 
        !iterationsInput || 
        iterationsInput.trim() === '' || 
        !iterations || 
        iterations < 1 || 
        isNaN(iterations) ||
        Number(iterationsInput) < 1;
      
      if (isEmptyOrInvalid) {
        actualIterations = 1;
        setIterations(1);
        setIterationsInput('1');
        setStoreMultiAgentIterations(1);
        showNotification('info', 'Iterations field was empty or invalid. Automatically set to 1.');
      }
      
      // ⚠️ КРИТИЧЕСКИ ВАЖНО: ПРАВИЛЬНЫЙ ПОРЯДОК ИНИЦИАЛИЗАЦИИ!
      // 1. Сначала очищаем все данные и состояния
      setMultiAgentIterationResults([]);
      setMultiAgentOptimizedPrompts([]);
      setMultiAgentIsOptimized(false);
      clearMultiAgentChat(); // Очищаем чаты
      setShowCompletionMessage(false); // Скрываем плашечку
      
      // 2. Устанавливаем параметры экспоненциального прогресса ДО включения оптимизации
      setMultiAgentCurrentProgressStage(0);
      setMultiAgentProgressStartTime(Date.now());
      setMultiAgentExpectedIterationTime(60); // 60 секунд на итерацию
      
      // 3. Обнуляем ВСЕ прогресс-бары синхронно
      setSmoothProgress(0);
      setMultiAgentOptimizationProgress(0);
      
      // 4. Теперь включаем оптимизацию (useEffect начнет обновлять прогресс)
      setMultiAgentIsOptimizing(true);

      // Подготавливаем данные для запроса
      const requestData: MultiAgentConfig = {
        dataset_name: datasetNameForRequest,
        iterations: actualIterations || storeIterations || 1,
        "master-agent": {
          name: "master",
          prompt: coordinatorPrompt,
          model_name: coordinatorModel,
          temperature: coordinatorTemperature,
          description: coordinatorDescription
        },
        "worker-agents": workers.map((worker, index) => ({
          name: worker.name || `Worker ${index + 1}`,
          prompt: worker.prompt,
          model_name: worker.model,
          temperature: worker.temperature,
          description: worker.description
        }))
      };

      console.log('📤 Multi-agent optimization request:', requestData);

      await unifiedWebSocketService.optimize(
        'multi',
        requestData,
        {
          onProgress: (progressValue) => {
            // ✅ Игнорируем прогресс от API, используем наш экспоненциальный
            // (экспоненциальный прогресс обновляется через useEffect)
            console.log('📊 Multi-agent optimization progress from API (ignored):', progressValue);
          },
          onComplete: async (result) => {
            console.log('✅ Multi-agent optimization completed:', result);
            
            // Завершаем оптимизацию
            setSmoothProgress(100);
            setMultiAgentOptimizationProgress(100);
            setMultiAgentIsOptimizing(false);
            setMultiAgentProgressStartTime(null);
            
            // Обрабатываем финальный результат
            processMultiAgentOptimizationResult(result);
          },
          onError: (error) => {
            console.error('❌ Multi-agent optimization error:', error);
            setMultiAgentIsOptimizing(false);
            setMultiAgentProgressStartTime(null);
            showNotification(
              'error',
              formatOptimizationWebSocketError(error, 'Оптимизация не удалась. Попробуйте ещё раз.')
            );
          },
              onIntermediateResult: (intermediateResult) => {
                console.log('📝 Processing multi-agent intermediate result:', intermediateResult);
                console.log('📊 Intermediate result structure:', {
                  hasPrompt: !!intermediateResult.prompt,
                  hasMasterAgent: !!intermediateResult["master-agent"],
                  hasWorkerAgents: !!intermediateResult["worker-agents"],
                  iteration: intermediateResult.iteration,
                  seq: intermediateResult.seq,
                  fullResult: intermediateResult
                });
                
                // Сохраняем результат в store независимо от наличия prompt
                // Потому что данные агентов могут быть важнее промпта
                const resultToStore = {
                  prompt: intermediateResult.prompt || 'Промпт отсутствует',
                  embedding_point: intermediateResult.embedding_point,
                  evaluation_point: intermediateResult.evaluation_point,
                  iteration: intermediateResult.iteration || (intermediateResult.seq !== undefined ? intermediateResult.seq : 1),
                  seq: intermediateResult.seq, // Передаем seq из WebSocket ответа
                  temperature: intermediateResult.temperature,
                  "master-agent": intermediateResult["master-agent"],
                  "worker-agents": intermediateResult["worker-agents"]
                };
                
                console.log('💾 Storing result in store:', resultToStore);
                addMultiAgentIterationResult(resultToStore);
              }
        }
      );

    } catch (error) {
      console.error('❌ Failed to start multi-agent optimization:', error);
      setMultiAgentIsOptimizing(false);
      showNotification(
        'error',
        formatOptimizationWebSocketError(error, 'Не удалось запустить оптимизацию. Попробуйте ещё раз.')
      );
    }
  };

  const handleStop = async () => {
    if (!multiAgentIsOptimizing || multiAgentStopping) {
      return;
    }

    setMultiAgentStopping(true);
    try {
      console.log('🛑 Stopping multi-agent optimization...');
      
      const runId = unifiedWebSocketService.getSessionId() || unifiedWebSocketService.getStoredRunId();
      
      if (runId) {
        await unifiedWebSocketService.stop(runId);
        console.log('✅ Stop command sent successfully');
      }
      
      setMultiAgentIsOptimizing(false);
      setMultiAgentProgressStartTime(null);
      setSmoothProgress(0);
      setMultiAgentOptimizationProgress(0);
      setShowCompletionMessage(false);
      setMultiAgentIsOptimized(false);
      
      unifiedWebSocketService.disconnect(true);
      
      showNotification('info', 'Оптимизация остановлена.');
    } catch (error) {
      console.error('❌ Failed to stop multi-agent optimization:', error);
      showNotification('error', 'Не удалось остановить оптимизацию. Попробуйте ещё раз.');
    } finally {
      setMultiAgentStopping(false);
    }
  };

  const addWorker = () => {
    if (multiAgentIsOptimizing) return;
    
    // Максимум 10 worker agents
    if (workers.length >= 10) {
      return;
    }
    
    // Для третьего и последующих worker агентов используем конфигурацию additional-worker
    const isAdditionalWorker = workers.length >= 2; // Индекс 2 и далее (3-й, 4-й и т.д.)
    
    const newWorker = {
      id: String(workers.length + 1),
      name: isAdditionalWorker ? additionalWorkerConfig.name : `Worker ${workers.length + 1}`,
      prompt: isAdditionalWorker ? additionalWorkerConfig.prompt : coordinatorPrompt,
      model: isAdditionalWorker ? additionalWorkerConfig.model : coordinatorModel,
      temperature: isAdditionalWorker ? additionalWorkerConfig.temperature : coordinatorTemperature,
      description: isAdditionalWorker ? additionalWorkerConfig.description : coordinatorDescription
    };
    
    const updatedWorkers = [...workers, newWorker];
    setWorkers(updatedWorkers);
    setWorkerAgents(updatedWorkers.length);
  };

  const removeWorker = () => {
    if (multiAgentIsOptimizing) return;
    if (workers.length > 1) {
      const updatedWorkers = workers.slice(0, -1);
      setWorkers(updatedWorkers);
      setWorkerAgents(updatedWorkers.length);
    }
  };

    const updateWorker = (id: string | number, field: string, value: any) => {
    if (multiAgentIsOptimizing) return;
    const updatedWorkers = workers.map(worker =>
      String(worker.id) === String(id)
        ? {
            ...worker,
            [field]: field === 'temperature' ? Math.min(value, 1) : value,
          }
        : worker
    );
    setWorkers(updatedWorkers);
  };

  const handleResetToDefaults = async () => {
    if (multiAgentIsOptimizing) return;
    if (isResetDisabled) return;
    setIsResetConfirmOpen(true);
  };

  const confirmResetToDefaults = async () => {
    if (isResettingDefaults) return;
    try {
      setIsResettingDefaults(true);
      await resetToDefaults();
      console.log('✅ Settings reset to defaults');
      showNotification('success', 'Настройки сброшены.');
      setIsResetConfirmOpen(false);
    } catch (error) {
      console.error('❌ Failed to reset settings:', error);
      showNotification('error', 'Не удалось сбросить настройки. Попробуйте ещё раз.');
    } finally {
      setIsResettingDefaults(false);
    }
  };

  return (
    <>
      <FloatingIcons />
      <ConfirmModal
        isOpen={isResetConfirmOpen}
        title="Сбросить настройки?"
        message="Текущая конфигурация будет перезаписана."
        cancelText="Отмена"
        confirmText="Сбросить"
        isConfirmLoading={isResettingDefaults}
        onCancel={() => {
          if (!isResettingDefaults) setIsResetConfirmOpen(false);
        }}
        onConfirm={confirmResetToDefaults}
      />
      <main>
        {/* Hero секция точно как на главной странице */}
        <section className="hero">
          <div className="hero-content">
            <h1 className="hero-title" id="hero-title">
              <span className="gradient-text">intellemma AI Tuner</span>
            </h1>
            <p className="hero-subtitle">
              Настройте конфигурацию ИИ-модели по датасету оценок: подберите промпты, модели и параметры автоматически.
            </p>
            <div className="hero-cta">
              <button className="cta-button primary" onClick={scrollToConfigure}>Начать</button>
              <button type="button" className="cta-button secondary" onClick={() => setShowOnboarding(true)}>
                Помощь
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="floating-elements">
              <div className="floating-element" data-speed="2">
                <i className="fas fa-brain"></i>
              </div>
              <div className="floating-element" data-speed="3">
                <i className="fas fa-robot"></i>
              </div>
              <div className="floating-element" data-speed="1.5">
                <i className="fas fa-code"></i>
              </div>
              <div className="floating-element" data-speed="2.5">
                <i className="fas fa-microchip"></i>
              </div>
            </div>
            <div className="hero-image">
              <div className="gradient-orb"></div>
            </div>
          </div>
        </section>

                       {/* Секция конфигурации мультиагентной системы */}
               <section className="configure-section">
                 <div className="container">
                   {/* Большая карточка конфигурации */}
                   <div className="configuration-card" id="configure-section">
                     <div className="section-header">
                       <h2 className="section-title">Настройка ИИ-ассистента</h2>
                       <div className="section-header-actions">
                        <HelpTourButton compact onClick={() => setShowOnboarding(true)} />
                      <button 
                        className="reset-defaults-btn"
                        onClick={handleResetToDefaults}
                        disabled={isResetDisabled}
                        style={isResetDisabled ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                        title="Вернуть все настройки к значениям по умолчанию"
                      >
                         <i className="fas fa-undo"></i>
                         Сбросить настройки
                       </button>
                     </div>
                     </div>
                     {/* Основные настройки оптимизации */}
                     <div className="main-settings">
              <div className="settings-grid">
                <div className="setting-item" id="evaluation-dataset">
                  <label>Датасет оценок</label>
                  <select
                    value={
                      selectedDataset && availableDatasets.includes(selectedDataset)
                        ? selectedDataset
                        : availableDatasets[0] ?? ''
                    }
                    onChange={(e) => setSelectedDataset(e.target.value)}
                    disabled={multiAgentIsOptimizing || availableDatasets.length === 0}
                    style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                    title={
                      multiAgentIsOptimizing
                        ? 'Дождитесь окончания оптимизации, чтобы менять параметры'
                        : ''
                    }
                  >
                    {availableDatasets.map((dataset) => (
                      <option key={dataset} value={dataset}>
                        {dataset}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="setting-item" id="iterations-setting">
                  <label>Итерации</label>
                  <input
                    type="number"
                    className="config-input"
                    value={iterationsInput}
                    min="1"
                    max="10"
                    disabled={multiAgentIsOptimizing}
                    style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                    onKeyDown={(e) => {
                      if (multiAgentIsOptimizing) {
                        e.preventDefault();
                        return;
                      }
                      // Разрешаем только цифры, Backspace, Delete, Arrow keys, Tab, Enter
                      const allowedKeys = [
                        'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
                        'Tab', 'Enter', 'Home', 'End'
                      ];
                      const isDigit = /^[0-9]$/.test(e.key);
                      const isAllowedKey = allowedKeys.includes(e.key);
                      
                      // Блокируем Ctrl/Cmd+A (выделение всего) через проверку модификаторов
                      if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
                        return; // Разрешаем Ctrl+A для выделения
                      }
                      
                      if (!isDigit && !isAllowedKey && !e.ctrlKey && !e.metaKey) {
                        e.preventDefault();
                        return false;
                      }
                    }}
                    onPaste={(e) => {
                      if (multiAgentIsOptimizing) {
                        e.preventDefault();
                        return;
                      }
                      e.preventDefault();
                      const pastedText = e.clipboardData.getData('text');
                      // Извлекаем только цифры из вставленного текста
                      const digitsOnly = pastedText.replace(/\D/g, '');
                      if (digitsOnly) {
                        const numValue = Number(digitsOnly);
                        if (numValue >= 1 && numValue <= 10) {
                          setIterationsInput(digitsOnly);
                          setIterations(numValue);
                          setStoreMultiAgentIterations(numValue);
                        } else if (numValue > 10) {
                          setIterationsInput('10');
                          setIterations(10);
                          setStoreMultiAgentIterations(10);
                        } else if (numValue < 1 && digitsOnly.length > 0) {
                          setIterationsInput('1');
                          setIterations(1);
                          setStoreMultiAgentIterations(1);
                        }
                      }
                    }}
                    onChange={(e) => {
                      if (multiAgentIsOptimizing) return;
                      const value = e.target.value;
                      // Фильтруем только цифры
                      const digitsOnly = value.replace(/\D/g, '');
                      if (digitsOnly === '') {
                        // Оставляем поле пустым, но устанавливаем 1 в store для валидации
                        setIterationsInput('');
                        setIterations(1);
                        setStoreMultiAgentIterations(1);
                        return;
                      }
                      
                      if (digitsOnly.length > 1 && digitsOnly.startsWith('0')) {
                        return;
                      }
                      
                      const numValue = Number(digitsOnly);
                      if (numValue <= 10) {
                        setIterationsInput(digitsOnly);
                        setIterations(numValue);
                        setStoreMultiAgentIterations(numValue);
                      } else {
                        setIterations(10);
                        setStoreMultiAgentIterations(10);
                        setIterationsInput('10');
                      }
                    }}
                    onBlur={(e) => {
                      if (multiAgentIsOptimizing) return;
                      const value = e.target.value;
                      if (value === '') {
                        // Оставляем поле пустым, но устанавливаем 1 в store для валидации
                        setIterations(1);
                        setStoreMultiAgentIterations(1);
                        return;
                      }
                      
                      const numValue = Number(value);
                      if (isNaN(numValue) || numValue < 1) {
                        setIterations(1);
                        setStoreMultiAgentIterations(1);
                        setIterationsInput('1');
                      } else if (numValue > 10) {
                        setIterations(10);
                        setStoreMultiAgentIterations(10);
                        setIterationsInput('10');
                      } else {
                        setIterations(numValue);
                        setStoreMultiAgentIterations(numValue);
                        setIterationsInput(numValue.toString());
                      }
                    }}
                  />
                </div>
              </div>
            </div>

                               {/* Конфигурация рабочих агентов */}
                   <div className="worker-config" id="worker-count">
                     <h3>Конфигурация агентов-исполнителей</h3>
                     <div className="worker-count">
                       <button 
                         className="count-btn"
                         onClick={removeWorker}
                         disabled={workers.length <= 1 || multiAgentIsOptimizing}
                         style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                       >
                         -
                       </button>
                       <span className="worker-number">{workerAgents}</span>
                       <button 
                         className="count-btn"
                         onClick={addWorker}
                         disabled={multiAgentIsOptimizing || workers.length >= 10}
                         style={(multiAgentIsOptimizing || workers.length >= 10) ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                       >
                         +
                       </button>
                     </div>
                   </div>

                   {/* Координатор агентов */}
                   <div className="agent-coordinator-section" id="coordinator-section">
                     <div className="user-coordinator-connection">
                      <div className="user-icon">
                         <i className="fas fa-user"></i>
                         <span>Пользователь</span>
                       </div>
                      <div className="connection-arrow"><i className="fas fa-arrows-up-down"></i></div>
                       <div className="coordinator-card">
                         <div className="card-header">
                           <i className="fas fa-robot"></i>
                           <span>Агент-координатор</span>
                         </div>
                         <div className="card-content">
                          <div className="input-group">
                            <label>Промпт</label>
                            <textarea 
                              className={isCoordinatorPromptInvalid ? 'input-error' : ''}
                              value={coordinatorPrompt}
                              onChange={(e) => {
                                setCoordinatorPrompt(e.target.value);
                                autoResizeTextarea(e.target);
                              }}
                              maxLength={MAX_COORDINATOR_PROMPT_LENGTH}
                              disabled={multiAgentIsOptimizing}
                              style={{
                                ...(multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}),
                                resize: 'none',
                                overflow: 'hidden',
                                minHeight: '60px'
                              }}
                              aria-invalid={isCoordinatorPromptInvalid}
                            />
                            <div style={{ fontSize: 12, color: '#5B7893', marginTop: 6 }}>
                              {(coordinatorPrompt || '').length}/{MAX_COORDINATOR_PROMPT_LENGTH} символов
                            </div>
                            {isCoordinatorPromptInvalid && (
                              <div className="field-error-text">Промпт обязателен.</div>
                            )}
                          </div>
                           <div className="input-group">
                             <label>Модель</label>
                             <div className="select-container">
                               <select 
                                 className="config-select"
                                 value={coordinatorModel} 
                                 onChange={(e) => setCoordinatorModel(e.target.value)}
                                 disabled={multiAgentIsOptimizing}
                                 style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                               >
                                 {configuratorModelOptions.map((model) => (
                                   <option key={model} value={model}>
                                     {model}
                                   </option>
                                 ))}
                               </select>
                               <i className="fas fa-chevron-down select-arrow"></i>
                             </div>
                           </div>
                           <div className="input-group">
                             <label>Температура: {coordinatorTemperature}</label>
                             <input 
                               type="range" 
                               min="0" 
                               max="1" 
                               step="0.01"
                               value={coordinatorTemperature} 
                               onChange={(e) => setCoordinatorTemperature(parseFloat(e.target.value))}
                               disabled={multiAgentIsOptimizing}
                               style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                             />
                           </div>
                           <div className="input-group">
                             <label>Описание</label>
                             <textarea 
                               value={coordinatorDescription}
                               onChange={(e) => {
                                 setCoordinatorDescription(e.target.value);
                                 autoResizeTextarea(e.target);
                               }}
                              maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
                               disabled={multiAgentIsOptimizing}
                               style={{
                                 ...(multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}),
                                 resize: 'none',
                                 overflow: 'hidden',
                                 minHeight: '60px'
                               }}
                               placeholder="Опишите роль агента-координатора…"
                             />
                            <div style={{ fontSize: 12, color: '#5B7893', marginTop: 6 }}>
                              {(coordinatorDescription || '').length}/{MAX_AGENT_DESCRIPTION_LENGTH} символов
                            </div>
                           </div>
                         </div>
                       </div>
                     </div>
                    <div className="connection-arrow-down"><i className="fas fa-arrows-up-down"></i></div>
                   </div>

                               {/* Рабочие агенты */}
                   <div className="agent-workers-section" id="workers-section">
                     <div className="workers-container">
                       <h3>Агенты-исполнители ({workerAgents})</h3>
                       <div className="workers-grid">
                          {workers.map((worker) => {
                            const isWorkerPromptInvalid = !worker.prompt.trim();

                            return (
                           <div key={worker.id} className="worker-card">
                             <div className="card-header">
                               <i className="fas fa-robot"></i>
                               <span>{worker.name || `Agent Worker ${worker.id}`}</span>
                             </div>
                             <div className="card-content">
                               <div className="input-group">
                                 <label>Имя агента</label>
                                 <input
                                   type="text"
                                   className="config-input"
                                   value={worker.name}
                                   onChange={(e) => {
                                    // Разрешаем только английские буквы и цифры
                                    const sanitized = e.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 60);
                                    updateWorker(worker.id, 'name', sanitized);
                                   }}
                                   onKeyDown={(e) => {
                                     // Блокируем пробелы и специальные символы
                                     if (e.key === ' ' || (!/^[A-Za-z0-9]$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Enter', 'Home', 'End'].includes(e.key))) {
                                       if (!(e.ctrlKey || e.metaKey)) {
                                         e.preventDefault();
                                       }
                                     }
                                   }}
                                   onPaste={(e) => {
                                     e.preventDefault();
                                     // Извлекаем только английские буквы и цифры из вставленного текста
                                     const pastedText = e.clipboardData.getData('text');
                                     const sanitized = pastedText.replace(/[^A-Za-z0-9]/g, '').slice(0, 60);
                                     if (sanitized) {
                                       updateWorker(worker.id, 'name', sanitized);
                                     }
                                   }}
                                  pattern="[A-Za-z0-9]*"
                                  title="Только латинские буквы (A–Z, a–z) и цифры (0–9), без пробелов и символов."
                                  maxLength={60}
                                   disabled={multiAgentIsOptimizing}
                                   style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                   placeholder="Введите имя агента…"
                                 />
                                <div style={{ fontSize: 12, color: '#5B7893', marginTop: 6 }}>
                                  {(worker.name?.length || 0)}/60 символов. Только латинские буквы (A–Z, a–z) и цифры (0–9), без пробелов и символов.
                                </div>
                               </div>
                              <div className="input-group">
                                 <label>Промпт</label>
                                 <textarea 
                                  className={isWorkerPromptInvalid ? 'input-error' : ''}
                                   value={worker.prompt}
                                   onChange={(e) => {
                                     updateWorker(worker.id, 'prompt', e.target.value);
                                     autoResizeTextarea(e.target);
                                   }}
                                  maxLength={MAX_AGENT_PROMPT_LENGTH}
                                   disabled={multiAgentIsOptimizing}
                                   style={{
                                     ...(multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}),
                                     resize: 'none',
                                     overflow: 'hidden',
                                     minHeight: '60px'
                                   }}
                                  aria-invalid={isWorkerPromptInvalid}
                                 />
                                <div style={{ fontSize: 12, color: '#5B7893', marginTop: 6 }}>
                                  {(worker.prompt?.length || 0)}/{MAX_AGENT_PROMPT_LENGTH} символов
                                </div>
                                {isWorkerPromptInvalid && (
                                  <div className="field-error-text">Промпт обязателен.</div>
                                )}
                               </div>
                               <div className="input-group">
                                 <label>Модель</label>
                                 <div className="select-container">
                                   <select 
                                     className="config-select"
                                     value={worker.model} 
                                     onChange={(e) => updateWorker(worker.id, 'model', e.target.value)}
                                     disabled={multiAgentIsOptimizing}
                                     style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                   >
                                     {configuratorModelOptions.map((model) => (
                                       <option key={model} value={model}>
                                         {model}
                                       </option>
                                     ))}
                                   </select>
                                   <i className="fas fa-chevron-down select-arrow"></i>
                                 </div>
                               </div>
                               <div className="input-group">
                                 <label>Температура: {worker.temperature}</label>
                                 <input 
                                   type="range" 
                                   min="0" 
                                   max="1" 
                                   step="0.01"
                                   value={worker.temperature} 
                                   onChange={(e) => updateWorker(worker.id, 'temperature', parseFloat(e.target.value))}
                                   disabled={multiAgentIsOptimizing}
                                   style={multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}}
                                 />
                               </div>
                               <div className="input-group">
                                 <label>Описание</label>
                                 <textarea 
                                   value={worker.description}
                                   onChange={(e) => {
                                     updateWorker(worker.id, 'description', e.target.value);
                                     autoResizeTextarea(e.target);
                                   }}
                                  maxLength={MAX_AGENT_DESCRIPTION_LENGTH}
                                   disabled={multiAgentIsOptimizing}
                                   style={{
                                     ...(multiAgentIsOptimizing ? { cursor: 'not-allowed', opacity: 0.6 } : {}),
                                     resize: 'none',
                                     overflow: 'hidden',
                                     minHeight: '60px'
                                   }}
                                   placeholder="Опишите роль этого агента-исполнителя…"
                                 />
                                <div style={{ fontSize: 12, color: '#5B7893', marginTop: 6 }}>
                                  {(worker.description?.length || 0)}/{MAX_AGENT_DESCRIPTION_LENGTH} символов
                                </div>
                               </div>
                             </div>
                           </div>
                         );
                         })}
                       </div>
                     </div>
                   </div>

                                 {/* Кнопка оптимизации */}
                     <div className="optimize-section" id="optimize-section">
                       <div className="optimize-row">
                         <button 
                           id="optimize-button"
                           className="optimize-btn" 
                           onClick={handleOptimize}
                           disabled={multiAgentIsOptimizing}
                         >
                           <i className="fas fa-magic"></i>
                           {multiAgentIsOptimizing ? 'Оптимизируем…' : 'Оптимизация'}
                         </button>
                         {(multiAgentIsOptimizing || multiAgentStopping) && (
                           <>
                             <button 
                               id="stop-button"
                               className="optimize-btn stop-btn" 
                               onClick={handleStop}
                               disabled={multiAgentStopping}
                               aria-busy={multiAgentStopping}
                               style={{
                                 background: multiAgentStopping ? '#C6E0F7' : '#FF8300',
                                 color: '#FFFFFF',
                                 marginLeft: '12px',
                                 cursor: multiAgentStopping ? 'not-allowed' : 'pointer',
                               }}
                             >
                               <i className={`fas ${multiAgentStopping ? 'fa-spinner fa-spin' : 'fa-stop'}`}></i>
                               {getJobStopButtonLabel(multiAgentStopping)}
                             </button>
                             <div className="circular-progress-container config-progress" data-label="Общий прогресс">
                               <div className={`circular-progress ${multiAgentIsOptimizing ? 'active' : ''}`} style={{ '--progress': `${smoothProgress.toFixed(1)}%` } as React.CSSProperties}>
                                <div className="circular-progress-value">{smoothProgress.toFixed(1)}%</div>
                               </div>
                             </div>
                           </>
                         )}
                       </div>
                     </div>
                     
                     
                     {/* Сообщение о завершении оптимизации */}
                     {showCompletionMessage && (
                       <div className="optimization-completion-message">
                         <div className="completion-icon">
                           <i className="fas fa-check-circle"></i>
                         </div>
                         <div className="completion-content">
                           <h4 className="completion-title">Оптимизация мультиагентной системы завершена</h4>
                           <p className="completion-description">
                             Мультиагентная система оптимизирована. Результаты и улучшения показаны ниже.
                           </p>
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
               </section>

               {/* Configuration evaluation секция */}
               <div id="visualization-section">
                 <MultiAgentConfigVisualization isConnector={false} />
               </div>

               {/* EVALUATION секция */}
               <section className="evaluation-section" id="evaluation-graphs">
                 <div className="container">
                   <h2 className="section-title">Оценка</h2>
                   <GraphsSection 
                     isOptimizing={multiAgentIsOptimizing}
                     progress={smoothProgress}
                     isMultiAgent={true}
                   />
                 </div>
               </section>

               {/* CHAT секция */}
               <section className="chat-section" id="chat-comparison">
                 <div className="container">
                   <h2 className="section-title">Чат</h2>
                   <MultiAgentChatComparison onShowLoginModal={onShowLoginModal} />
                 </div>
               </section>
      </main>
      
      <MultiAgentOnboarding 
        isOpen={showOnboarding}
        onClose={() => setShowOnboarding(false)}
      />
      
      {/* Уведомление о пустом iterations */}
      <NotificationWrapper
        type={notification.type}
        message={notification.message}
        isVisible={notification.isVisible}
        onClose={hideNotification}
        position="top-right"
        duration={NOTIFICATION_AUTO_HIDE_MS}
      />
    </>
  );
};

export default MultiAgentPage;
